import { isNative, hasNativePlugin } from './is-native';

/**
 * Hand a document (an authenticated PDF route) to the user to keep, print or
 * forward.
 *
 * Why this can't just be `<a href="/api/invoices/x/pdf" download>`:
 *
 * - **Android WebView has no PDF renderer and Capacitor sets no
 *   DownloadListener**, so navigating to a PDF URL in the app does nothing at
 *   all. Silently. That's what "I press it and nothing happens" was.
 * - **`target="_blank"` is worse.** Capacitor's iOS delegate answers
 *   `createWebViewWith` by calling `UIApplication.shared.open(url)` and
 *   returning nil, so the authenticated URL opens in *Safari* with no session
 *   and lands on the login wall. On Android a non-app-host navigation is thrown
 *   to `ACTION_VIEW`, i.e. the system browser, same session problem.
 * - **`Browser.open()` doesn't help either.** Chrome Custom Tabs and
 *   SFSafariViewController have their own cookie jars, so an auth-gated URL
 *   still hits the login wall. That's the trap the old blob-in-a-new-window
 *   code was trying to dodge.
 *
 * So on native: fetch the bytes over the same-origin request (the session
 * cookie rides along), write them to the cache directory, and hand the file URI
 * to the share sheet — which is also what the user actually wants, since it
 * reaches Files, Drive, print and WhatsApp. On web, a plain object-URL download.
 *
 * Both @capacitor/filesystem and @capacitor/share are in every shipped binary,
 * so this needs no native build. `hasNativePlugin` is still checked because an
 * older binary in the field is always possible.
 *
 * Returns true when the document was handed off, false when the caller should
 * show its own error.
 */
export async function saveOrShareDocument(
  pdfUrl: string,
  filename: string,
): Promise<boolean> {
  try {
    const res = await fetch(pdfUrl, { credentials: 'same-origin' });
    if (!res.ok) return false;
    return await saveOrShareBlob(await res.blob(), filename);
  } catch {
    return false;
  }
}

/**
 * The second half of `saveOrShareDocument`, for a caller that has already
 * fetched the file — the chat's export download reads the response itself so
 * a refusal can be shown in the server's own words (anti-pattern #35).
 */
export async function saveOrShareBlob(blob: Blob, filename: string): Promise<boolean> {
  try {
    if (isNative() && hasNativePlugin('Filesystem') && hasNativePlugin('Share')) {
      const [{ Filesystem, Directory }, { Share }] = await Promise.all([
        import('@capacitor/filesystem'),
        import('@capacitor/share'),
      ]);

      const base64 = await blobToBase64(blob);
      // Cache, not Documents: this is a hand-off copy, not the user's library,
      // and the OS is free to reclaim it. file_paths.xml already exposes
      // cache-path for the FileProvider the share sheet needs on Android.
      await Filesystem.writeFile({ path: filename, data: base64, directory: Directory.Cache });
      const { uri } = await Filesystem.getUri({ path: filename, directory: Directory.Cache });
      await shareOrCancel({ title: filename, files: [uri] }, Share);
      return true;
    }

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
    return true;
  } catch {
    return false;
  }
}

/**
 * Hand a document straight to whatever the person shares with — WhatsApp, Mail,
 * Messages, AirDrop.
 *
 * Distinct from `saveOrShareDocument` by INTENT rather than by mechanism. On
 * native both end at the same OS sheet, because that sheet is genuinely the
 * platform's answer to both "keep this" and "send this to someone", and there
 * is nothing to gain from inventing a difference. What differs is the web:
 * saving downloads the file, while sharing asks the browser to share it and
 * only falls back to a download when it cannot.
 *
 * That split is the whole reason this exists. The share sheet was previously
 * only reachable through a button labelled "Save PDF", so on a phone the way to
 * WhatsApp a quote to a customer was to press Save and hope.
 *
 * `navigator.canShare({ files })` is the guard that matters: Chrome on the
 * desktop defines `navigator.share` but refuses files, so feature-detecting on
 * `share` alone throws at the point of use rather than falling back cleanly.
 *
 * Returns true when the document was handed off, false when the caller should
 * show its own error. A share the person cancels still counts as handed off —
 * they saw the sheet and chose; that is not a failure to report back at them.
 */
export async function shareDocument(
  pdfUrl: string,
  filename: string,
): Promise<boolean> {
  try {
    const res = await fetch(pdfUrl, { credentials: 'same-origin' });
    if (!res.ok) return false;
    const blob = await res.blob();

    if (isNative() && hasNativePlugin('Filesystem') && hasNativePlugin('Share')) {
      const [{ Filesystem, Directory }, { Share }] = await Promise.all([
        import('@capacitor/filesystem'),
        import('@capacitor/share'),
      ]);
      const base64 = await blobToBase64(blob);
      // Cache, not Documents — see saveOrShareDocument on why.
      await Filesystem.writeFile({ path: filename, data: base64, directory: Directory.Cache });
      const { uri } = await Filesystem.getUri({ path: filename, directory: Directory.Cache });
      await shareOrCancel({ title: filename, files: [uri] }, Share);
      return true;
    }

    const file = new File([blob], filename, { type: 'application/pdf' });
    if (typeof navigator !== 'undefined'
        && typeof navigator.canShare === 'function'
        && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({ files: [file], title: filename });
      } catch (err) {
        // AbortError is the person closing the sheet. Anything else is a real
        // failure and should fall through to the download rather than leaving
        // them with nothing.
        if ((err as { name?: string })?.name !== 'AbortError') throw err;
      }
      return true;
    }

    // No file sharing here — hand them the file instead of doing nothing.
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
    return true;
  } catch {
    return false;
  }
}

/**
 * Get a document to a printer.
 *
 * There's no one API for this, so it's the best route each platform offers:
 *
 * - **In a browser**, fetch the PDF and print it from a hidden iframe so the OS
 *   print dialog opens straight away, with the ACTUAL PDF the customer receives —
 *   not a canvas bitmap of it, and not the separately-styled HTML print view. See
 *   printViaHiddenFrame for the two traps that makes non-obvious. If it doesn't
 *   take, this falls back to opening the PDF in a tab: one extra tap rather than
 *   a dead end.
 * - **In the app**, `window.print()` is a no-op in WKWebView and unreliable in
 *   Android WebView, so the real PDF goes to the system share sheet — which is
 *   literally where Print lives on iOS, and reaches Android's print service. A
 *   true one-tap print here would need a native print plugin, i.e. a new
 *   TestFlight + Play build, which isn't worth one tap.
 *
 * Returns false when the caller should show its own error.
 */
export async function printDocument(pdfUrl: string, filename: string): Promise<boolean> {
  if (isNative()) return saveOrShareDocument(pdfUrl, filename);
  if (typeof window === 'undefined') return false;
  if (await printViaHiddenFrame(pdfUrl)) return true;
  // Couldn't drive the print dialog — get them to the PDF anyway.
  return !!window.open(pdfUrl, '_blank', 'noopener,noreferrer');
}

/**
 * Print a PDF without leaving the page.
 *
 * Two non-obvious details, both learned the hard way:
 *
 * 1. **It has to be a `blob:` URL, not the route URL.** The app sends
 *    `X-Frame-Options: DENY` and `frame-ancestors 'none'`, which block framing
 *    even SAME-ORIGIN — so pointing an iframe at /api/…/pdf is refused, silently
 *    (no `onerror`). A blob is a client-side object with no HTTP headers, so the
 *    frame policy doesn't apply and no security header has to be relaxed. The
 *    bytes still come from the authenticated route over a same-origin fetch.
 * 2. **The iframe can't be 0×0 or `display:none`.** Chrome renders PDFs in an
 *    out-of-process plugin that may not initialise in a zero-area frame, and
 *    `print()` then does nothing. So it's full-size but fully transparent and
 *    non-interactive, sitting behind the page.
 *
 * The frame must also stay in the document while the dialog is open — pulling it
 * out cancels the print — hence cleanup on a timer.
 *
 * Resolves false rather than throwing or hanging, so the caller can fall back to
 * simply opening the PDF.
 */
async function printViaHiddenFrame(pdfUrl: string): Promise<boolean> {
  let blobUrl: string | null = null;
  try {
    const res = await fetch(pdfUrl, { credentials: 'same-origin' });
    if (!res.ok) return false;
    blobUrl = URL.createObjectURL(await res.blob());
  } catch {
    return false;
  }

  const url = blobUrl;
  return new Promise(resolve => {
    let settled = false;
    const done = (ok: boolean, removeNow: boolean) => {
      if (settled) return;
      settled = true;
      if (removeNow) {
        frame.remove();
        URL.revokeObjectURL(url);
      } else {
        // Long enough for the dialog to have been raised and dismissed.
        window.setTimeout(() => { frame.remove(); URL.revokeObjectURL(url); }, 60_000);
      }
      resolve(ok);
    };

    const frame = document.createElement('iframe');
    frame.setAttribute('aria-hidden', 'true');
    frame.setAttribute('tabindex', '-1');
    frame.style.cssText =
      'position:fixed;inset:0;width:100%;height:100%;border:0;opacity:0;pointer-events:none;z-index:-1;';

    frame.onload = () => {
      try {
        const w = frame.contentWindow;
        if (!w) return done(false, true);
        w.focus();
        w.print();
        done(true, false);
      } catch {
        done(false, true);
      }
    };
    frame.onerror = () => done(false, true);

    // Belt and braces: if onload never fires (a viewer that can't display it),
    // don't leave the button spinning.
    window.setTimeout(() => done(false, true), 5000);

    frame.src = url;
    document.body.appendChild(frame);
  });
}

/**
 * The person closing the share sheet is a choice, not a failure.
 *
 * Capacitor's `Share.share` REJECTS when the sheet is dismissed without picking
 * anything ("Share canceled" on both iOS and Android), so without this a
 * cancel fell into the caller's catch and came back as false, and the screen
 * said the file had not saved when the person had simply changed their mind.
 * The web half already treats `AbortError` this way; this is the native half.
 * Anything that is not a cancel still throws.
 */
export function isShareCancel(err: unknown): boolean {
  const e = err as { name?: string; message?: string } | null;
  return e?.name === 'AbortError' || /cancel/i.test(e?.message ?? '');
}

async function shareOrCancel(
  opts: { title: string; files: string[] },
  Share: { share: (o: { title: string; files: string[] }) => Promise<unknown> },
): Promise<void> {
  try {
    await Share.share(opts);
  } catch (err) {
    if (!isShareCancel(err)) throw err;
  }
}

/** Filesystem.writeFile wants base64, and it must not include the data: prefix.
 *  Exported for `save-media.ts`, which does the same native dance for photos
 *  and videos — one copy of the "strip the data: prefix" detail, not two. */
export function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Could not read the document'));
    reader.onloadend = () => {
      const result = String(reader.result || '');
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.readAsDataURL(blob);
  });
}

/**
 * Hand a customer-facing LINK to whatever the owner shares with.
 *
 * The sibling of `shareDocument`, and the one that should win wherever a live
 * page exists: a PDF is a photocopy of a moment, while the link stays correct,
 * can be accepted or paid, and tells the owner it was opened.
 *
 * 🚨 The URL must already be in hand when this is called. `navigator.share`
 * requires transient user activation, and an `await fetch(...)` inside the click
 * handler spends it — Safari then throws `NotAllowedError` and the sheet never
 * opens. That is why the callers pre-fetch the link when the document opens
 * rather than when the button is pressed.
 *
 * Falls back to the clipboard rather than to nothing, because "copied" is a
 * perfectly good outcome on a desktop browser with no share sheet — the owner
 * pastes it into whatever they were already typing in.
 *
 * Returns 'shared' when the OS sheet took it, 'copied' when it went to the
 * clipboard instead (the caller should say so — a silent copy looks like a dead
 * button), and false when neither worked.
 */
export async function shareCustomerLink(
  url: string,
  opts: { title: string; text: string },
): Promise<'shared' | 'copied' | false> {
  try {
    if (isNative() && hasNativePlugin('Share')) {
      const { Share } = await import('@capacitor/share');
      // Both `text` and `url`: iOS reads the url and ignores stray text, while
      // Android's chooser hands most apps the text field only — passing one
      // without the other loses the link in whichever half you skipped.
      await Share.share({ title: opts.title, text: `${opts.text} ${url}`, url });
      return 'shared';
    }

    if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
      try {
        await navigator.share({ title: opts.title, text: opts.text, url });
        return 'shared';
      } catch (err) {
        // The person closing the sheet is a choice, not a failure — don't then
        // shove it on their clipboard behind their back.
        if ((err as { name?: string })?.name === 'AbortError') return 'shared';
        // Anything else (no activation left, unsupported) falls through.
      }
    }

    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(url);
      return 'copied';
    }

    return false;
  } catch {
    return false;
  }
}
